<template>
  <ExpandableCard
    :id="id"
    v-model:expanded="isExpanded"
    class="route-builder-rate-shop-card"
    :class="[type, { 'has-error': isAnyErrors }]"
    tabindex="-1"
  >
    <template #default>
      <div class="left">
        <div class="header-container cell">
          <div class="header">{{ header }}</div>
          <div v-if="subheader" class="subheader">{{ subheader }}</div>
        </div>

        <KeyValuePair
          :label="t('common.totalParam', { param: t('shipments.transitTime') })"
          :value="
            totalTransitTime
              ? t('rateShop.countDays', { count: totalTransitTime })
              : '-'
          "
          :loading="isAnyRateShopLoading"
          class="cell"
          inline
        />

        <KeyValuePair
          :label="t('common.totalParam', { param: t('common.distances') })"
          :value="t('common.paramMilesAbbr', { param: Math.round(mileage) })"
          :loading="isAnyRateShopLoading"
          class="cell"
          inline
        />
      </div>

      <div class="right">
        <InfoIcon
          v-if="isAnyShipmentMissingRateQuote"
          icon="pi pi-exclamation-triangle"
          :tooltip="t('routeBuilder.noRateQuoteWarning', 2)"
          :tooltip-disabled="currentStep !== 'rating'"
        />

        <KeyValuePair
          v-tooltip="{
            value: t('routeBuilder.currencyMismatch'),
            // 0 currencies means no rates, which is fine and shouldn't show a warning
            disabled: currenciesForRateQuotes.length <= 1,
          }"
          :label="t('common.totalParam', { param: t('rateShop.price') })"
          :value="
            currency ? renderMoney(totalPrice, { currencyCode: currency }) : '-'
          "
          :loading="isAnyRateShopLoading"
          class="total-price"
          :class="{
            'text-success': !isAnyErrors && !isAnyShipmentMissingRateQuote,
            'text-warning': isAnyShipmentMissingRateQuote,
          }"
          inline
        />

        <RygenButton
          v-if="currentStep === 'rating'"
          mode="secondary"
          :label="t('rateShop.select')"
          :disabled="isAnyRateShopLoading"
          @click="onSelect(type)"
        />
      </div>
    </template>

    <template #expansion>
      <RouteBuilderPhase2RateShopTable
        :id="`${id}-table`"
        :rate-quotes="rateQuotes"
        :type="type"
        :current-step="currentStep"
      />
    </template>
  </ExpandableCard>
</template>

<script lang="ts" setup>
import { useConfirm } from 'primevue/useconfirm';
import type { PropType, Ref } from 'vue';
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useTooltip } from '@rygen/rygen-ui/composables';
import { renderMoney } from '@rygen/rygen-ui/i18n';
import { Loadable } from '@rygen/rygen-ui/model';
import { dedupeArray, notNull } from '@rygen/rygen-ui/util';

import { RateQuoteGroupType } from '@/model/routeBuilder';
import {
  RouteBuilderPhase2Step,
  RoutePlanShipmentRequest,
} from '@/model/routeBuilder/phase2';

import RouteBuilderPhase2RateShopTable from '@/components/routeBuilder/rateShop/RouteBuilderPhase2RateShopTable.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import ExpandableCard from '@rygen/rygen-ui/components/ui/ExpandableCard.vue';
import InfoIcon from '@rygen/rygen-ui/components/ui/InfoIcon.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';

const isExpanded = defineModel<boolean>('expanded');

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  type: {
    type: String as PropType<RateQuoteGroupType>,
    required: true,
  },
  mileage: {
    type: Number,
    required: true,
  },
  rateQuotes: {
    type: Array as PropType<Array<Loadable<RoutePlanShipmentRequest>>>,
    required: true,
  },
  currentStep: {
    type: String as PropType<RouteBuilderPhase2Step>,
    default: () => 'rating',
  },
});
const { id, type, mileage, rateQuotes, currentStep } = toRefs(props);
const emit = defineEmits<{
  (event: 'select', type: RateQuoteGroupType): void;
}>();

const confirm = useConfirm();
const { t } = useI18n();
const vTooltip = useTooltip();

type RateQuoteGroupData = { header: string; subheader?: string };
const rateQuoteGroups: Ref<Record<RateQuoteGroupType, RateQuoteGroupData>> =
  computed(() => ({
    preferred: {
      header: t('routeBuilder.rateQuoteGroupType.preferred.header'),
    },
    fastest: {
      header: t('routeBuilder.rateQuoteGroupType.fastest.header'),
      subheader: t('routeBuilder.rateQuoteGroupType.fastest.subheader'),
    },
    cheapest: {
      header: t('routeBuilder.rateQuoteGroupType.cheapest.header'),
      subheader: t('routeBuilder.rateQuoteGroupType.cheapest.subheader'),
    },
    none: {
      header: t('shipments.shipments', rateQuotes.value.length),
    },
  }));

const currentRateQuoteGroup = computed(() => rateQuoteGroups.value[type.value]);
const header = computed(() => currentRateQuoteGroup.value.header);
const subheader = computed(() => currentRateQuoteGroup.value.subheader);

const isAnyRateShopLoading = computed(() =>
  rateQuotes.value.some((r) => r.loading),
);

const isAnyErrors = computed(() => rateQuotes.value.some((r) => !!r.error));

const actualQuotes = computed(() => {
  return rateQuotes.value.map(({ data }) => data?.rateQuote).filter(notNull);
});

const totalTransitTime = computed(() => {
  return actualQuotes.value
    .map((r) => r.transitTime)
    .filter(notNull)
    .reduce((total, curr) => total + curr, 0);
});

const totalPrice = computed(() => {
  return actualQuotes.value
    .map((r) => r.totalPrice)
    .filter(notNull)
    .reduce((total, curr) => total + curr, 0);
});

const currenciesForRateQuotes = computed(() => {
  const currencies = actualQuotes.value
    .map((r) => r.currencyCode)
    .filter(notNull);
  return dedupeArray(currencies);
});

const currency = computed(() => {
  return currenciesForRateQuotes.value.length === 1
    ? currenciesForRateQuotes.value[0]
    : undefined;
});

const shipmentsMissingRateQuote = computed(() => {
  return rateQuotes.value.filter((r) => {
    return !r.loading && !r.error && !r.data?.rateQuote;
  });
});

const isAnyShipmentMissingRateQuote = computed(() => {
  return shipmentsMissingRateQuote.value.length > 0;
});

const onSelect = (type: RateQuoteGroupType) => {
  if (!isAnyShipmentMissingRateQuote.value) {
    emit('select', type);
    return;
  }

  confirm.require({
    group: 'rygen-confirm-dialog',
    header: t(
      'routeBuilder.createWithoutRateHeader',
      shipmentsMissingRateQuote.value.length,
    ),
    message: t(
      'routeBuilder.createWithoutRate',
      shipmentsMissingRateQuote.value.length,
    ),
    accept: () => emit('select', type),
    acceptClass: 'p-button-sm',
    acceptIcon: 'pi pi-check',
    acceptLabel: t('common.confirm'),
    rejectClass: 'p-button-sm p-button-text',
    rejectLabel: t('common.cancel'),
  });
};
</script>

<style>
.route-builder-rate-shop-card {
  --route-builder-rate-shop-card-block-padding: calc(
    var(--sp-3) - var(--route-builder-rate-shop-padding)
  );
  --route-builder-rate-shop-card-inline-padding: calc(
    var(--sp-6) - var(--route-builder-rate-shop-padding)
  );
  --route-builder-rate-shop-card-border-color: var(--p-gray-500);
  border-left: 4px solid var(--route-builder-rate-shop-card-border-color);

  &.none,
  &.preferred {
    --route-builder-rate-shop-card-border-color: var(--p-green-600);
  }

  &.fastest {
    --route-builder-rate-shop-card-border-color: var(--p-sky-600);
  }

  &.cheapest {
    --route-builder-rate-shop-card-border-color: var(--p-cyan-600);
  }

  &.has-error {
    --route-builder-rate-shop-card-border-color: var(--danger-color);
  }

  .p-card-body {
    padding: 0;

    .expandable-card-body {
      padding-block: var(--route-builder-rate-shop-card-block-padding);
      padding-inline: var(--route-builder-rate-shop-card-inline-padding);

      .expandable-card-content {
        display: flex;
        gap: var(--gap-1);
        justify-content: space-between;
        align-items: center;

        .left {
          display: flex;
          align-items: center;

          .header-container {
            display: flex;
            flex-flow: column;
            gap: var(--sp-1);

            .header {
              font-weight: 600;
              color: var(--p-gray-600);
            }

            .subheader {
              color: var(--p-gray-400);
            }
          }

          .cell {
            padding: var(--sp-3);
            flex: 1 0 200px;

            &:not(:first-of-type) {
              border-left: 1px solid var(--divider-color);
              margin-left: var(--sp-3);
            }

            &.header-container {
              padding: var(--sp-3) var(--sp-3) var(--sp-3) 0;
              flex: 1 0 150px;
            }
          }
        }

        .right {
          display: flex;
          gap: var(--gap-1);
          align-items: center;

          .info-icon-container {
            color: var(--warning-color);
          }
        }

        .key-value-pair {
          &.key-value-pair-inline {
            .key-value-pair-label {
              font-weight: 600;

              &::after {
                content: ': ';
              }
            }
          }
        }
      }
    }

    .expandable-card-expansion {
      border-top: 0;
      padding: 0 var(--route-builder-rate-shop-card-inline-padding) var(--sp-6);
    }
  }
}
</style>
